import React, { useState, useMemo, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Collapse,
  Tooltip,
  Autocomplete,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import { tableCellClasses } from "@mui/material/TableCell";
import { ArrowBack, FileDownload, Logout, Search, FilterList, Clear, Save } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { attendanceAPI, attendanceAdjustmentsAPI, flexiDaysAPI, employeeAPI } from '../services/api';

// Height reduced 0.9x, then another 0.9x on top (~45px, from the default MUI medium ~56px).
const compactFieldSx = {
  '& .MuiOutlinedInput-root, & .MuiInputBase-root': { height: 45 },
  '& .MuiOutlinedInput-input, & .MuiSelect-select, & .MuiAutocomplete-input': {
    paddingTop: '7px',
    paddingBottom: '7px',
  },
  // Default label rest-position (translateY 16px) is tuned for the default 56px-tall
  // field; with the field shortened to 45px this re-centers the resting label.
  '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
    transform: 'translate(14px, 11px) scale(1)',
  },
};

const AttendanceReport = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Calculate previous month and year
  const currentDate = new Date();
  const previousMonth = currentDate.getMonth(); // getMonth() returns 0-11
  const previousYear = previousMonth === 0 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
  const previousMonthValue = previousMonth === 0 ? 12 : previousMonth;
  
  const [month, setMonth] = useState(previousMonthValue);
  const [year, setYear] = useState(previousYear);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState('');

  // Flexi Days & OT State
  const [flexiDays, setFlexiDays] = useState({});
  // Standalone numeric note next to Total - never factored into Total or
  // any other calculation, saved the same way as flexiDays.
  const [exDays, setExDays] = useState({});
  const [otDetails, setOtDetails] = useState({}); // { [emp_id]: { ot_hours, ot_amt, ot_total } }
  const [dirtyEmployees, setDirtyEmployees] = useState(new Set());
  const [savingFlexiDays, setSavingFlexiDays] = useState(false);

  // Employee Filter State
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [showFilter, setShowFilter] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('');

  // Single-employee, multiple-months view: null = off, page behaves exactly
  // as it does today. Once an employee is picked, Month becomes a
  // multi-select and each selected month comes back as its own row for
  // just that employee - read-only (editing is out of scope here, since
  // the per-row edit state below is keyed only by emp_id and would clash
  // across multiple months for the same employee).
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [multiMonthEmployee, setMultiMonthEmployee] = useState(null);
  const [multiMonths, setMultiMonths] = useState([]);
  const isMultiMonthMode = !!multiMonthEmployee;

  useEffect(() => {
    attendanceAPI.getEmployeesLookup()
      .then((res) => setEmployeeOptions(res.data || []))
      .catch(() => setEmployeeOptions([]));
  }, []);



  // Adjustment Dialog State (single cell)
  const [adjustmentDialogOpen, setAdjustmentDialogOpen] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [savingAdjustment, setSavingAdjustment] = useState(false);

  // Comment Dialog State
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [commentEmp, setCommentEmp] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [savingComment, setSavingComment] = useState(false);

  // Row Edit Dialog State (all days for one employee)
  const [rowEditDialogOpen, setRowEditDialogOpen] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [rowEdits, setRowEdits] = useState({});
  const [rowEditsInitial, setRowEditsInitial] = useState({});
  const [savingRowEdits, setSavingRowEdits] = useState(false);
  // One-level undo for "Revert All to Original" - holds whatever rowEdits
  // held right before the revert wiped it out, so "Undo Revert All" can put
  // it straight back (whether that was the dialog's just-loaded state or
  // edits the admin was already mid-way through). Null when there's nothing
  // to undo (dialog just opened, or the undo was already used).
  const [preRevertRowEdits, setPreRevertRowEdits] = useState(null);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i);

  // Check if user is admin
  // Read-only in the single-employee, multiple-months view - see
  // isMultiMonthMode's declaration above for why.
  const userIsAdmin = isAdmin() && !isMultiMonthMode;

  // Shared styles for native inputs — defined once, not recreated per row
  const nativeInputStyle = { width: 70, fontSize: '12px', padding: '5px 8px', border: '1px solid #ccc', borderRadius: 4, outline: 'none', boxSizing: 'border-box' };
  const nativeSelectStyle = { fontSize: '12px', padding: '4px 6px', border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', outline: 'none' };

  const STATUS_OPTIONS = [
    { value: 'P',        label: 'P - Present' },
    { value: 'CO',       label: 'CO - Comp Off (Counted as Present)' },
    { value: 'Half P',   label: '1/2PA - Half Present Half Absent' },
    { value: 'A',        label: 'A - Absent' },
    { value: 'L',        label: 'L - Leave' },
    { value: 'Half L',   label: '1/2PL - Half Present, Grant Half Leave' },
    { value: 'WO',       label: 'WO - Week Off' },
    { value: 'H',        label: 'H - Holiday' },
    { value: 'WOP',      label: 'WOP - Week Off Present' },
    { value: 'HP',       label: 'HP - Holiday Present' },
    { value: 'WOHalf P', label: 'WO1/2P - Week Off Day 1/2 Present' },
    { value: 'HHalf P',  label: 'H1/2P - Holiday, 1/2 Day Present' },
  ];

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    setShowFilter(false);
    setEmployeeFilter('');

    try {
      let response;
      if (isMultiMonthMode) {
        if (multiMonths.length === 0) {
          setError('Pick at least one month.');
          setLoading(false);
          return;
        }
        const monthYearPairs = [...multiMonths].sort((a, b) => a - b).map((m) => ({ month: m, year }));
        const raw = await attendanceAPI.getEmployeeMonthsReport(multiMonthEmployee.emp_id, monthYearPairs);
        // Each row is the same employee, one per selected month - give it a
        // synthetic, month-unique emp_id so the existing per-row state below
        // (keyed by emp_id everywhere) can't collide across months, and
        // fold the month into the displayed name so every existing render
        // path (which just shows emp.emp_name verbatim) needs no changes.
        const data = raw.data.data.map((row) => ({
          ...row,
          emp_id: `${row.emp_id}::${row.year}-${row.month}`,
          emp_name: `${row.emp_name} — ${months[row.month - 1]} ${row.year}`,
        }));
        response = { data: { report_type: 'regular', data } };
      } else {
        response = await attendanceAPI.getRegularReport(month, year);
      }

      setReportData(response.data);
      setShowFilter(true);

      // Initialize flexi days and OT details from backend response
      const initialFlexiDays = {};
      const initialExDays = {};
      const initialOtDetails = {};
      response.data.data.forEach(emp => {
        initialFlexiDays[emp.emp_id] = emp.flexi_days || 0;
        initialExDays[emp.emp_id] = emp.ex_days || 0;
        const otHours = emp.ot_hours || 0;
        const otAmt   = emp.ot_amt   || 0;
        const otRate  = emp.ot_rate  || 0;
        // Same value already shown read-only in the "OT Days" column - days
        // worked on a week-off or holiday, each counting as a full (or half)
        // day of overtime.
        const otDays = emp.emp_ot_allowed
          ? (emp.week_off_present + 0.5 * emp.week_off_half + emp.holiday_present + 0.5 * emp.holiday_half)
          : 0;
        // null (not 0) means Gross Salary genuinely isn't configured for this
        // employee - the OT Days term falls back to 0 in the total, but the
        // UI flags it rather than showing a silent, indistinguishable zero.
        const grossSalary = emp.gross_salary ?? null;
        initialOtDetails[emp.emp_id] = {
          ot_hours:        otHours,
          ot_amt:          otAmt,
          ot_rate:         otRate,
          ot_days:         otDays,
          gross_salary:    grossSalary,
          ot_total:        otDays * ((grossSalary ?? 0) / 30) + otHours * otRate + otAmt,
          adv_amt:         emp.adv_amt || 0,
          review_required: emp.review_required ?? 'N',
        };
      });
      setFlexiDays(initialFlexiDays);
      setExDays(initialExDays);
      setOtDetails(initialOtDetails);
      setDirtyEmployees(new Set());
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh — re-fetches report data without resetting filters or showing a full loading state.
  // Called after any adjustment save/delete so leave metrics are recomputed server-side.
  const refreshReport = async () => {
    try {
      const response = await attendanceAPI.getRegularReport(month, year);
      setReportData(response.data);
    } catch {
      // Silently ignore — the stale data is still visible; user can manually refresh
    }
  };

  // PERFORMANCE OPTIMIZED: Filter employees using useMemo
  // Filters by Employee Name, Employee ID, and Department
  // PERFORMANCE OPTIMIZED: Filter employees using useMemo
  const filteredEmployees = useMemo(() => {
    if (!reportData || !reportData.data) return [];

    if (!employeeFilter.trim()) {
      return reportData.data;
    }

    const searchTerm = employeeFilter.toLowerCase().trim();

    return reportData.data.filter(emp =>
      emp.emp_name.toLowerCase().includes(searchTerm) ||
      emp.emp_id.toLowerCase().includes(searchTerm) ||
      (emp.emp_dept || '').toLowerCase().includes(searchTerm)
    );
  }, [reportData, employeeFilter]);

  // Review filter as a separate pass — never affects name/ID filter performance
  const displayEmployees = useMemo(() => {
    if (!reviewFilter) return filteredEmployees;
    return filteredEmployees.filter(emp => (emp.review_required ?? 'N') === reviewFilter);
  }, [filteredEmployees, reviewFilter]);

  const markDirty = (empId) => setDirtyEmployees(prev => new Set([...prev, empId]));

  // Only mark an employee dirty (and thus save-worthy) when a value
  // genuinely changes - clicking into a field without changing it (or the
  // Review dropdown re-firing onChange with its existing value) used to
  // mark it dirty regardless, which created meaningless all-zero
  // emp_flexi_days rows on the next Save.
  const handleFlexiDaysChange = (empId, value) => {
    const parsed = parseFloat(value) || 0;
    if (flexiDays[empId] === parsed) return;
    setFlexiDays(prev => ({ ...prev, [empId]: parsed }));
    markDirty(empId);
  };

  const handleExDaysChange = (empId, value) => {
    const parsed = parseFloat(value) || 0;
    if (exDays[empId] === parsed) return;
    setExDays(prev => ({ ...prev, [empId]: parsed }));
    markDirty(empId);
  };

  const handleOtChange = (empId, field, value) => {
    const parsed = parseFloat(value) || 0;
    const current = otDetails[empId] || {};
    if (current[field] === parsed) return;
    setOtDetails(prev => {
      const updated = { ...(prev[empId] || {}) };
      updated[field] = parsed;
      updated.ot_total =
        (updated.ot_days ?? 0) * ((updated.gross_salary ?? 0) / 30) +
        (updated.ot_hours ?? 0) * (updated.ot_rate ?? 0) +
        (updated.ot_amt ?? 0);
      return { ...prev, [empId]: updated };
    });
    markDirty(empId);
  };

  const handleReviewChange = (empId, value) => {
    const current = otDetails[empId] || {};
    if (current.review_required === value) return;
    setOtDetails(prev => ({
      ...prev,
      [empId]: { ...(prev[empId] || {}), review_required: value },
    }));
    markDirty(empId);
  };

  const buildSavePayload = (empId, empName) => ({
    emp_id:    empId,
    emp_name:  empName,
    month,
    year,
    flexi_days:      flexiDays[empId] || 0,
    ex_days:         exDays[empId] || 0,
    ot_hours:        otDetails[empId]?.ot_hours        ?? 0,
    ot_amt:          otDetails[empId]?.ot_amt           ?? 0,
    ot_total:        otDetails[empId]?.ot_total         ?? 0,
    adv_amt:         otDetails[empId]?.adv_amt          ?? 0,
    review_required: otDetails[empId]?.review_required ?? null,
  });

  const handleSaveFlexiDays = async (empId, empName) => {
    // Guards the same gap the dirty-check fixes for Save All - without
    // this, clicking the per-row Save icon on an untouched employee would
    // still write a meaningless all-zero row, since this path never
    // consulted dirtyEmployees at all.
    if (!dirtyEmployees.has(empId)) {
      setSaveMessage('No changes to save.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }
    setSavingFlexiDays(true);
    setSaveMessage('');
    try {
      await flexiDaysAPI.createOrUpdate(buildSavePayload(empId, empName));
      setSaveMessage(`Saved for ${empName}`);
      setDirtyEmployees(prev => { const next = new Set(prev); next.delete(empId); return next; });
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingFlexiDays(false);
    }
  };

  const handleSaveAllFlexiDays = async () => {
    const dirtyEmps = reportData.data.filter(emp => dirtyEmployees.has(emp.emp_id));
    if (dirtyEmps.length === 0) {
      setSaveMessage('No changes to save.');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }
    setSavingFlexiDays(true);
    setSaveMessage('');
    try {
      const items = dirtyEmps.map(emp => buildSavePayload(emp.emp_id, emp.emp_name));
      const res = await flexiDaysAPI.bulkSave(items);
      setSaveMessage(`Saved ${res.data.saved} modified record(s)`);
      setDirtyEmployees(new Set());
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingFlexiDays(false);
      setTimeout(() => setSaveMessage(''), 5000);
    }
  };

  // Multi-month rows carry their own month/year (one row per month); a
  // single-month row doesn't, so falls back to the page's selected month/year.
  const getRowMonthLabel = (emp) => {
    const m = emp.month ?? month;
    const y = emp.year ?? year;
    return `${months[m - 1]} ${y}`;
  };

  const calculateTotalWorkingDays = (emp) => {
    const flexi = flexiDays[emp.emp_id] || 0;
    const rawWo = emp.week_off + emp.week_off_present + emp.week_off_half;
    const cappedWo = (emp.wo_count != null) ? Math.min(rawWo, emp.wo_count) : rawWo;
    return (
      emp.present +
      cappedWo +
      emp.holiday +
      emp.holiday_present +
      emp.leave +
      (emp.half_leave || 0) +
      emp.holiday_half +
      0.5 * emp.half_present +
      flexi
    );
  };

  // How many fewer WO days than the calendar baseline (emp.wo_count) an
  // employee should be expected to have, based on how little of the month
  // they actually worked (the Total column) - someone who barely worked
  // this month hasn't earned a full month's worth of Week Offs either.
  // Deepest band wins (not additive) - e.g. Total=10 applies the <14 band
  // (expected -2), not <14 and <24 stacked together.
  const getAdjustedWoExpected = (baseWoCount, total) => {
    if (total < 5) return 0;
    if (total < 7) return baseWoCount - 3;
    if (total < 16) return baseWoCount - 2;
    if (total < 24) return baseWoCount - 1;
    return baseWoCount;
  };

  // emp.wo_count is the backend's calendar occurrence count for this
  // employee's week_off_day in that row's month (e.g. 4 or 5 Sundays) -
  // null when no week_off_day is configured at all. That calendar count is
  // then adjusted down via getAdjustedWoExpected based on how much of the
  // month was actually worked (the Total column), and the result compared
  // against the actual WO-family total (WO + WOP + WO1/2P) to flag a drift
  // - e.g. an admin edit converted a day to/from WO, or Flexi Week Off's
  // one-time auto-pick was itself edited afterward, or the employee simply
  // wasn't around enough of the month to have earned the full WO quota.
  // Applies the same way to Flexi Week Off employees too.
  const getWoMismatchInfo = (emp) => {
    if (emp.wo_count == null) return null;
    const actual = (emp.week_off || 0) + (emp.week_off_present || 0) + (emp.week_off_half || 0);
    const total = calculateTotalWorkingDays(emp);
    const expected = getAdjustedWoExpected(emp.wo_count, total);
    return { actual, expected, baseExpected: emp.wo_count, total, mismatch: actual !== expected };
  };

  // The Salary Webpage's actual final "pay" column, not the bare Pay
  // Salary figure - PF Enabled employees show Total Payable Salary (Pay
  // Salary - Advance - PF - ESI, no OT), everyone else shows the regular
  // Net Salary (Pay Salary + OT - Advance). Same formulas as
  // salaryCalculations.js's calculateRow, using the OT Total/Adv Amt
  // already live-edited on this screen (otDetails) so it reflects
  // whatever's currently entered, not a stale saved value. Screen-only
  // (see the export code, which never touches this).
  //
  // Advance subtracted here is Adv. Amt (this screen's own field) PLUS
  // Adv. Given (the Salary Advance page's contribution) combined - same as
  // how the real Salary Webpage builds its advance_deduction (see
  // app/api/payroll.py's _apply_salary_advances: both sources are additive,
  // not one-or-the-other), so this "Salary" figure reflects real take-home,
  // not just what's visible on this one screen.
  const calculatePaySalary = (emp) => {
    if (emp.gross_salary == null) return null;
    // consider_30_days: always divide by a flat 30 instead of the real
    // days-in-month, so this employee's effective daily rate stays stable
    // every month instead of drifting between 28/30/31-day months.
    const daysInMonth = emp.consider_30_days ? 30 : new Date(year, month, 0).getDate();
    const rawPaySalary = emp.gross_salary * (calculateTotalWorkingDays(emp) / daysInMonth);
    const paySalary = Math.ceil(Math.round(rawPaySalary * 100) / 100);
    const advanceDeduction = Number(otDetails[emp.emp_id]?.adv_amt ?? 0) + Number(emp.adv_given ?? 0);

    if (emp.is_pf_enabled) {
      const basicSalary = Math.round(paySalary * 0.5);
      const pf = Math.round(basicSalary * 0.12);
      const esi = Math.round(paySalary * 0.0075);
      const totalDeduction = pf + esi;
      return paySalary - advanceDeduction - totalDeduction;
    }

    const overtimeAmount = Number(otDetails[emp.emp_id]?.ot_total ?? 0);
    const paySalaryWithOt = Math.round(paySalary + overtimeAmount);
    return Math.round(paySalaryWithOt - advanceDeduction);
  };

  const handleExport = async () => {
    try {
      const response = await attendanceAPI.exportRegular(month, year);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `regular_attendance_${year}_${String(month).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export report');
    }
  };

  // Raw punch-log OT sheet (Employee Name, OT Allowed, Date, P1-P4,
  // Hrs Worked) - a separate export straight off the Attendance punch
  // table, not the processed report - see backend's /attendance/export/ot.
  const handleExportOT = async () => {
    try {
      const response = await attendanceAPI.exportOT(month, year);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ot_report_${year}_${String(month).padStart(2, '0')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to export OT report');
    }
  };

  const handleClearFilter = () => {
    setEmployeeFilter('');
  };

  const formatStatusDisplay = (status) => {
    if (!status) return status;
    // return status.replace(/Half/g, '1/2');
    return status.replace(/Half P/g, '1/2P').replace(/Half L/g, '1/2PL');
  };

  const abbreviateWeekOff = (day) => {
    const map = { Sunday: 'Sun', Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed', Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat', 'Alternate Sunday': 'Alt Sun' };
    return map[day] || day;
  };

  const getDayAbbreviation = (dayNumber) => {
    const date = new Date(year, month - 1, dayNumber);
    const dayNames = ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa'];
    return dayNames[date.getDay()];
  };

  const formatDayHeader = (dayNumber) => {
    return `${dayNumber}${getDayAbbreviation(dayNumber)}`;
  };

  const getStatusColor = (status) => {
    // P, CO (Comp Off - behaves like P)
    if (status === 'P' || status === 'CO') return '#ffffff';

    // L 
    if (status === 'L') return '#dddddd';
    
    // 1/2L - Half Leave
    if (status === 'Half L') return '#dddddd';

    // 1/2P
    if (status === 'Half P') return '#ffffdd';
    
    // A - Red background
    if (status === 'A') return '#ffcdcd';
    
    // WO, H - Grey background
    if (status === 'WO' || status === 'H') return '#dddddd';
    
    // WOP, WO1/2P, HP H1/2P 
    if (status === "WOP" || status === "WOHalf P" || status === "HP" || status === "HHalf P") return '#d9f6ff';
    return 'inherit';
  };

  const getStatusTextColor = (status) => {
    // P, CO, WOP, HP - White text on green
    if (status === 'P' || status === 'CO' || status === 'WOP' || status === 'HP') return '#666666';
    
    // L - Light grey text on purple
    if (status === 'L') return '#666666';
    
    // 1/2P, WO1/2P, H1/2P - Light orange text
    if (status.includes('Half P')) return '#666666';
    
    // A - Light red text
    if (status === 'A') return '#666666';
    
    // WO, H - Light grey text
    if (status === 'WO' || status === 'H') return '#666666';
    
    return '#666666';
  };

  const calculateDetailedMetrics = (days) => {
    const metrics = {
      half_present: 0,
      present: 0,
      absent: 0,
      leave: 0,
      half_leave: 0,  
      holiday: 0,
      holiday_present: 0,
      holiday_half: 0,
      week_off: 0,
      week_off_present: 0,
      week_off_half: 0,
    };
    
    days.forEach(day => {
      const status = day.status;
      const normalized = status.replace('Half P', '1/2P').replace('Half L', '1/2L');
      
      if (normalized === '1/2P' || status === 'Half P') metrics.half_present++;
      else if (normalized === '1/2L' || status === 'Half L') metrics.half_leave++;
      else if (normalized === 'P' || normalized === 'CO') metrics.present++;
      else if (normalized === 'A') metrics.absent++;
      else if (normalized === 'L') metrics.leave++;
      else if (normalized === 'H') metrics.holiday++;
      else if (normalized === 'HP') metrics.holiday_present++;
      else if (normalized === 'H1/2P' || status === 'HHalf P') metrics.holiday_half++;
      else if (normalized === 'WO') metrics.week_off++;
      else if (normalized === 'WOP') metrics.week_off_present++;
      else if (normalized === 'WO1/2P' || status === 'WOHalf P') metrics.week_off_half++;
    });
    
    return metrics;
  };

  const handleCellClick = (emp, day) => {
    if (!userIsAdmin) return;
    
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day.date).padStart(2, '0')}`;
    
    setSelectedCell({
      empId: emp.emp_id,
      empName: emp.emp_name,
      date: dateStr,
      dayNumber: day.date,
      currentStatus: day.status,
      originalStatus: day.original_status || day.status,
      isAdjusted: day.is_adjusted || false,
    });
    setNewStatus(day.status);
    setAdjustmentReason('');
    setError('');
    setAdjustmentDialogOpen(true);
  };

  // Extracted so the multi-month view (rows with different day-counts - a
  // 30-day June row alongside a 31-day July row) can pad each row out to a
  // shared 31-column header without duplicating this cell's markup, and
  // without changing anything about the normal single-month path below.
  // Half P (or its WO/Holiday variants) caused by a genuinely missing punch
  // (odd punch count - a real data gap), not a short-but-complete shift.
  // Mutually exclusive with is_half_day_threshold by construction - the
  // backend only ever sets that flag for a real, complete punch pair - but
  // guarded here too in case a future change ever lets both apply at once.
  const isMissingPunchHalfDay = (day) =>
    day.needs_review &&
    !day.is_half_day_threshold &&
    ['Half P', 'WOHalf P', 'HHalf P'].includes(day.status);

  const renderDayCell = (emp, day) => (
    <TableCell
      key={day.date}
      align="center"
      onClick={() => handleCellClick(emp, day)}
      sx={{
        bgcolor: getStatusColor(day.status),
        color: getStatusTextColor(day.status),
        fontWeight: 'bold',
        border: day.is_absent_threshold
          ? '4px solid #e57373 !important'
          : isMissingPunchHalfDay(day)
          ? '4px solid #ffcdd2 !important'
          : day.is_half_day_threshold
          ? '4px solid #ffd79c !important'
          : 'none',
        cursor: userIsAdmin ? 'pointer' : 'default',
        position: 'relative',
        '&:hover': userIsAdmin ? { opacity: 0.8, boxShadow: '0 0 10px rgba(0,0,0,0.3)' } : {},
      }}
      title={
        day.is_manual_adjustment
          ? `Adjusted: ${formatStatusDisplay(day.status)} (Original: ${formatStatusDisplay(day.original_status)})${userIsAdmin ? ' - Click to edit' : ''}`
          : day.is_absent_threshold
          ? `Marked Absent: worked under the department's Absent Threshold${userIsAdmin ? ' - Click to edit' : ''}`
          : isMissingPunchHalfDay(day)
          ? `Half P: caused by a missing punch (incomplete punch data), not a short shift${userIsAdmin ? ' - Click to edit' : ''}`
          : day.is_half_day_threshold
          ? `Half P: worked less than half-day (short duration, not a missing punch)${userIsAdmin ? ' - Click to edit' : ''}`
          : userIsAdmin ? `${formatStatusDisplay(day.status)} - Click to edit` : formatStatusDisplay(day.status)
      }
    >
      {formatStatusDisplay(day.status)}
      {day.is_manual_adjustment && day.status !== day.original_status && (
        <Box component="span" sx={{ position: 'absolute', top: 1, left: 2, fontSize: '0.55rem', fontWeight: 'bold', lineHeight: 1, color: '#d32f2f', opacity: 0.9 }}>
          {formatStatusDisplay(day.original_status)}
        </Box>
      )}
    </TableCell>
  );

  // Multi-month rows can have different day-counts (June=30, July=31,
  // Feb=28/29) - render a fixed 1-31 column set and leave a blank cell for
  // any date this row's month doesn't actually have, instead of letting a
  // shorter month's cells shift left of where the header says they are.
  const renderPaddedDayCells = (emp) => {
    const byDate = new Map(emp.days.map((d) => [d.date, d]));
    return Array.from({ length: 31 }, (_, i) => i + 1).map((dayNum) => {
      const day = byDate.get(dayNum);
      return day
        ? renderDayCell(emp, day)
        : <TableCell key={dayNum} align="center" sx={{ bgcolor: '#f5f5f5' }} />;
    });
  };

  const handleSaveAdjustment = async () => {
    setSavingAdjustment(true);
    setError('');
    
    try {
      await attendanceAdjustmentsAPI.create({
        emp_id: selectedCell.empId,
        adjustment_date: selectedCell.date,
        original_status: selectedCell.originalStatus,
        adjusted_status: newStatus,
        reason: adjustmentReason || null,
      });
      
      setReportData(prevData => {
        const newData = { ...prevData };
        const employees = [...newData.data];
        
        const empIndex = employees.findIndex(e => e.emp_id === selectedCell.empId);
        if (empIndex === -1) return prevData;
        
        const employee = { ...employees[empIndex] };
        const days = [...employee.days];
        
        const dayIndex = days.findIndex(d => d.date === selectedCell.dayNumber);
        if (dayIndex === -1) return prevData;
        
        days[dayIndex] = {
          ...days[dayIndex],
          status: newStatus,
          original_status: selectedCell.originalStatus,
          is_adjusted: true,
          needs_review: false
        };
        
        const metrics = calculateDetailedMetrics(days);
        const total = metrics.present + metrics.week_off_present + metrics.holiday_present + metrics.half_leave +
                     metrics.holiday_half +
                     0.5 * (metrics.half_present + metrics.week_off_half);
        
        employee.days = days;
        employee.half_present = metrics.half_present;
        employee.present = metrics.present;
        employee.absent = metrics.absent;
        employee.leave = metrics.leave;
        employee.half_leave = metrics.half_leave;
        employee.holiday = metrics.holiday;
        employee.holiday_present = metrics.holiday_present;
        employee.holiday_half = metrics.holiday_half;
        employee.week_off = metrics.week_off;
        employee.week_off_present = metrics.week_off_present;
        employee.week_off_half = metrics.week_off_half;
        employee.total_working_days = total;
        
        employees[empIndex] = employee;
        newData.data = employees;
        
        return newData;
      });
      
      setSaveMessage(`Attendance adjusted for ${selectedCell.empName} on day ${selectedCell.dayNumber}`);
      setAdjustmentDialogOpen(false);
      setTimeout(() => setSaveMessage(''), 3000);
      refreshReport();

    } catch (err) {
      setError('Failed to save adjustment: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleDeleteAdjustment = async () => {
    setSavingAdjustment(true);
    setError('');
    
    try {
      await attendanceAdjustmentsAPI.delete(selectedCell.empId, selectedCell.date);
      
      setReportData(prevData => {
        const newData = { ...prevData };
        const employees = [...newData.data];
        
        const empIndex = employees.findIndex(e => e.emp_id === selectedCell.empId);
        if (empIndex === -1) return prevData;
        
        const employee = { ...employees[empIndex] };
        const days = [...employee.days];
        
        const dayIndex = days.findIndex(d => d.date === selectedCell.dayNumber);
        if (dayIndex === -1) return prevData;
        
        days[dayIndex] = {
          ...days[dayIndex],
          status: selectedCell.originalStatus,
          original_status: null,
          is_adjusted: false
        };
        
        const metrics = calculateDetailedMetrics(days);
        const total = metrics.present + metrics.week_off_present + metrics.holiday_present + metrics.half_leave +
                     metrics.holiday_half +
                     0.5 * (metrics.half_present + metrics.week_off_half);
        
        employee.days = days;
        employee.half_present = metrics.half_present;
        employee.present = metrics.present;
        employee.absent = metrics.absent;
        employee.leave = metrics.leave;
        employee.half_leave = metrics.half_leave;
        employee.holiday = metrics.holiday;
        employee.holiday_present = metrics.holiday_present;
        employee.holiday_half = metrics.holiday_half;
        employee.week_off = metrics.week_off;
        employee.week_off_present = metrics.week_off_present;
        employee.week_off_half = metrics.week_off_half;
        employee.total_working_days = total;
        
        employees[empIndex] = employee;
        newData.data = employees;
        
        return newData;
      });
      
      setSaveMessage(`Adjustment reverted for ${selectedCell.empName} on day ${selectedCell.dayNumber}`);
      setAdjustmentDialogOpen(false);
      setTimeout(() => setSaveMessage(''), 3000);
      refreshReport();

    } catch (err) {
      setError('Failed to delete adjustment: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingAdjustment(false);
    }
  };

  const handleEmpNameClick = (emp) => {
    if (!userIsAdmin) return;
    setCommentEmp(emp);
    setCommentText(emp.emp_comment || '');
    setCommentDialogOpen(true);
  };

  const handleSaveComment = async () => {
    setSavingComment(true);
    setError('');
    try {
      await employeeAPI.update(commentEmp.emp_id, { comment: commentText });
      setReportData(prevData => ({
        ...prevData,
        data: prevData.data.map(e =>
          e.emp_id === commentEmp.emp_id ? { ...e, emp_comment: commentText } : e
        ),
      }));
      setSaveMessage(`Comment saved for ${commentEmp.emp_name}`);
      setCommentDialogOpen(false);
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      setError('Failed to save comment: ' + (err.response?.data?.detail || 'Unknown error'));
    } finally {
      setSavingComment(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // ── Row-level edit handlers ───────────────────────────────────────────────

  const handleEmpIdClick = (emp) => {
    if (!userIsAdmin) return;
    setSelectedEmp(emp);
    const initial = {};
    emp.days.forEach(day => {
      initial[day.date] = { status: day.status, reason: '' };
    });
    setRowEditsInitial(initial);
    setRowEdits(JSON.parse(JSON.stringify(initial)));
    setPreRevertRowEdits(null);
    setRowEditDialogOpen(true);
  };

  const handleRowEditChange = (dayNumber, field, value) => {
    setRowEdits(prev => ({
      ...prev,
      [dayNumber]: { ...prev[dayNumber], [field]: value },
    }));
  };

  // Sets every day's status to its true original (pre-adjustment) value -
  // day.original_status where one exists (an adjusted day), or day.status
  // unchanged otherwise (already the original, nothing to revert). Reason
  // text is cleared along with it, since a reverted day has no adjustment
  // left to explain. Snapshots the current rowEdits first so Undo Revert
  // All can restore exactly what was here before this ran.
  const handleRevertAllToOriginal = () => {
    if (!selectedEmp) return;
    setPreRevertRowEdits(rowEdits);
    const reverted = {};
    selectedEmp.days.forEach(day => {
      reverted[day.date] = { status: day.original_status || day.status, reason: '' };
    });
    setRowEdits(reverted);
  };

  // Puts back whatever rowEdits held immediately before the last Revert All
  // to Original - a single-level undo, consumed once used (clicking Revert
  // All again starts a fresh undo snapshot).
  const handleUndoRevertAll = () => {
    if (!preRevertRowEdits) return;
    setRowEdits(preRevertRowEdits);
    setPreRevertRowEdits(null);
  };

  const handleSaveRowEdits = async () => {
    setSavingRowEdits(true);
    setError('');

    const modifiedDays = selectedEmp.days.filter(day =>
      rowEdits[day.date]?.status !== rowEditsInitial[day.date]?.status
    );

    if (modifiedDays.length === 0) {
      setRowEditDialogOpen(false);
      setSavingRowEdits(false);
      return;
    }

    const bulkItems = modifiedDays.map(day => {
      const edit = rowEdits[day.date];
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day.date).padStart(2, '0')}`;
      const isRevert = day.is_adjusted && edit.status === day.original_status;
      return isRevert
        ? { action: 'delete', emp_id: selectedEmp.emp_id, adjustment_date: dateStr }
        : {
            action: 'upsert',
            emp_id: selectedEmp.emp_id,
            adjustment_date: dateStr,
            original_status: day.original_status || day.status,
            adjusted_status: edit.status,
            reason: edit.reason?.trim() || null,
          };
    });

    let savedCount = 0;
    const saveErrors = [];

    try {
      const result = await attendanceAdjustmentsAPI.bulkSave(bulkItems);
      savedCount = (result.data.saved || 0) + (result.data.deleted || 0);
    } catch (err) {
      saveErrors.push(err.response?.data?.detail || 'Bulk save failed');
    }

    // Update main table state for saved days
    setReportData(prevData => {
      const newData = { ...prevData };
      const employees = [...newData.data];
      const empIndex = employees.findIndex(e => e.emp_id === selectedEmp.emp_id);
      if (empIndex === -1) return prevData;

      const employee = { ...employees[empIndex] };
      const days = employee.days.map(day => {
        const edit = rowEdits[day.date];
        const initial = rowEditsInitial[day.date];
        if (edit && initial && edit.status !== initial.status) {
          const isRevert = day.is_adjusted && edit.status === day.original_status;
          if (isRevert) {
            return {
              ...day,
              status: edit.status,
              original_status: null,
              is_adjusted: false,
              needs_review: false,
            };
          }
          return {
            ...day,
            status: edit.status,
            original_status: day.original_status || day.status,
            is_adjusted: true,
            needs_review: false,
          };
        }
        return day;
      });

      const metrics = calculateDetailedMetrics(days);
      const total =
        metrics.present + metrics.week_off_present + metrics.holiday_present +
        metrics.half_leave +
        metrics.holiday_half +
        0.5 * (metrics.half_present + metrics.week_off_half);

      employee.days = days;
      employee.half_present      = metrics.half_present;
      employee.present           = metrics.present;
      employee.absent            = metrics.absent;
      employee.leave             = metrics.leave;
      employee.half_leave        = metrics.half_leave;
      employee.holiday           = metrics.holiday;
      employee.holiday_present   = metrics.holiday_present;
      employee.holiday_half      = metrics.holiday_half;
      employee.week_off          = metrics.week_off;
      employee.week_off_present  = metrics.week_off_present;
      employee.week_off_half     = metrics.week_off_half;
      employee.total_working_days = total;

      employees[empIndex] = employee;
      newData.data = employees;
      return newData;
    });

    if (saveErrors.length === 0) {
      setSaveMessage(`Saved ${savedCount} adjustment(s) for ${selectedEmp.emp_name}`);
    } else {
      setSaveMessage(`Saved ${savedCount} adjustments. Errors: ${saveErrors.join('; ')}`);
    }

    setSavingRowEdits(false);
    setRowEditDialogOpen(false);
    setTimeout(() => setSaveMessage(''), 5000);
    refreshReport();
  };

  return (
    <Box>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 2 }}>
            Attendance Report
          </Typography>
          <Typography variant="body1" sx={{ mr: 2 }}>
            {user?.full_name}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Autocomplete
              sx={{ minWidth: 192, ...compactFieldSx }}
              options={employeeOptions}
              getOptionLabel={(opt) => opt ? opt.emp_name : ''}
              isOptionEqualToValue={(opt, val) => opt.emp_id === val.emp_id}
              value={multiMonthEmployee}
              onChange={(e, newVal) => { setMultiMonthEmployee(newVal); setMultiMonths([]); }}
              renderInput={(params) => <TextField {...params} label="Employee (optional)" />}
            />

            {!isMultiMonthMode ? (
              <FormControl sx={{ minWidth: 120, ...compactFieldSx }}>
                <InputLabel>Month</InputLabel>
                <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
                  {months.map((m, idx) => (
                    <MenuItem key={idx} value={idx + 1}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl sx={{ minWidth: 176, ...compactFieldSx }}>
                <InputLabel>Months</InputLabel>
                <Select
                  multiple
                  value={multiMonths}
                  label="Months"
                  onChange={(e) => setMultiMonths(e.target.value)}
                  input={<OutlinedInput label="Months" />}
                  renderValue={(selected) => selected.map((m) => months[m - 1]).join(', ')}
                >
                  {months.map((m, idx) => (
                    <MenuItem key={idx} value={idx + 1}>
                      <Checkbox checked={multiMonths.includes(idx + 1)} />
                      <ListItemText primary={m} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControl sx={{ minWidth: 90, ...compactFieldSx }}>
              <InputLabel>Year</InputLabel>
              <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
                {years.map(y => (
                  <MenuItem key={y} value={y}>{y}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button variant="contained" color="primary" startIcon={<Search />} onClick={fetchReport} disabled={loading}>
              {loading ? 'Searching...' : 'Search'}
            </Button>

            <Button variant="contained" startIcon={<FileDownload />} onClick={handleExport} disabled={isMultiMonthMode || !reportData || loading} title={isMultiMonthMode ? 'Not available in multi-month view' : ''}>
              EXCEL
            </Button>

            <Button variant="contained" startIcon={<FileDownload />} onClick={handleExportOT} disabled={isMultiMonthMode || loading} title={isMultiMonthMode ? 'Not available in multi-month view' : 'Download OT punch-log sheet'}>
              OT
            </Button>

            {!isMultiMonthMode && reportData && reportData.data.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleSaveAllFlexiDays}
                disabled={savingFlexiDays}
              >
                {savingFlexiDays ? 'Saving...' : 'Save All'}
              </Button>
            )}

            {/* Filters - Right aligned */}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
              {showFilter && reportData && reportData.data.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 90 }}>
                  <InputLabel>Review</InputLabel>
                  <Select
                    value={reviewFilter}
                    label="Review"
                    onChange={(e) => setReviewFilter(e.target.value)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Y">Y</MenuItem>
                    <MenuItem value="N">N</MenuItem>
                  </Select>
                </FormControl>
              )}
              <Collapse in={showFilter && reportData && reportData.data.length > 0} orientation="horizontal">
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Filter by Employee or Dept..."
                  value={employeeFilter}
                  onChange={(e) => setEmployeeFilter(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterList />
                      </InputAdornment>
                    ),
                    endAdornment: employeeFilter && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearFilter} edge="end">
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: 240,
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'background.paper',
                    }
                  }}
                />
              </Collapse>
            </Box>
          </Box>

          {/* Employee count below the controls */}
          {(employeeFilter || reviewFilter) && reportData && reportData.data.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Showing {displayEmployees.length} of {reportData.data.length} employees
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {saveMessage && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaveMessage('')}>{saveMessage}</Alert>}
          {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>}

          {!loading && !reportData && (
            <Alert severity="info">Please select month, year, and report type, then click "Search" to view attendance report.</Alert>
          )}

          {!loading && reportData && reportData.data.length === 0 && (
            <Alert severity="info">No attendance data available for {months[month - 1]} {year}</Alert>
          )}

          {!loading && reportData && reportData.data.length > 0 && displayEmployees.length === 0 && (
            <Alert severity="warning">No employees found matching the current filters</Alert>
          )}

          {!loading && reportData && displayEmployees.length > 0 && (
            <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
              <Table stickyHeader size="small" sx={{[`& .${tableCellClasses.root}`]: {
                fontSize: "12px",
                padding: "8px 4px",
                border: "1px solid rgba(224, 224, 224, 1)", // Standard MUI border color
                // "&:last-child": {
                //   border: "none", // Remove the border from the last cell in each row
                // },
          },}}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', position: 'sticky', left: 0, zIndex: 3, borderRight: '2px solid #ddd' }}>Emp ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', position: 'sticky', left: 100, zIndex: 3, borderRight: '2px solid #ddd', minWidth: isMultiMonthMode ? 220 : undefined, whiteSpace: isMultiMonthMode ? 'nowrap' : 'normal' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Dept</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>WO</TableCell>
                    
                    {isMultiMonthMode
                      ? Array.from({ length: 31 }, (_, i) => i + 1).map(dayNum => (
                          <TableCell key={dayNum} sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 60 }} align="center">
                            {dayNum}
                          </TableCell>
                        ))
                      : reportData.data[0]?.days.map(day => (
                          <TableCell key={day.date} sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', minWidth: 60 }} align="center">
                            {formatDayHeader(day.date)}
                          </TableCell>
                        ))}

                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 50 }} align="center">1/2P</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 40 }} align="center">P</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 40 }} align="center">A</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 40 }} align="center">L</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 40 }} align="center">H</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 50 }} align="center">HP</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 60 }} align="center">H1/2P</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 50 }} align="center">WO</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 55 }} align="center">WOP</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', minWidth: 70 }} align="center">WO1/2P</TableCell>
                    
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 80 }} align="center">Flexi Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 60 }} align="center">Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 80 }} align="center">Ex Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 60 }} align="center">OT Days</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 80 }} align="center">OT Hours</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 80 }} align="center">OT Amt</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 80 }} align="center">OT Total</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 70 }} align="center">Review</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 80 }} align="center">OthAdv. Adj</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 90 }} align="center">Adv. Amt</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white' }} align="center">Actions</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', bgcolor: '#9b559d', color: 'white', minWidth: 90 }} align="center">Salary</TableCell>

                    {/* Leave tracking columns — orange headers */}
                    {['CF Leaves', 'Leaves p.a.', 'Total Avail.', 'Earned', 'Availed', 'Eligible Remaining', 'Remaining'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 'bold', bgcolor: '#e65100', color: 'white', minWidth: 70, whiteSpace: 'nowrap' }} align="center">{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayEmployees.map(emp => {
                    const woInfo = getWoMismatchInfo(emp);
                    return (
                    <TableRow key={emp.emp_id} hover>
                      <Tooltip title={userIsAdmin ? 'Click to edit all days for this employee' : ''} placement="right">
                        <TableCell
                          onClick={() => handleEmpIdClick(emp)}
                          sx={{
                            position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1,
                            borderRight: '2px solid #ddd',
                            cursor: userIsAdmin ? 'pointer' : 'default',
                            color: userIsAdmin ? 'primary.main' : 'inherit',
                            fontWeight: userIsAdmin ? 'bold' : 'normal',
                            '&:hover': userIsAdmin ? { textDecoration: 'underline', opacity: 0.8 } : {},
                          }}
                        >
                          {isMultiMonthMode ? emp.emp_id.split('::')[0] : emp.emp_id}
                        </TableCell>
                      </Tooltip>
                      <TableCell
                        onClick={() => handleEmpNameClick(emp)}
                        sx={{
                          position: 'sticky', left: 100, bgcolor: 'background.paper', zIndex: 1,
                          borderRight: '2px solid #ddd',
                          cursor: userIsAdmin ? 'pointer' : 'default',
                          '&:hover': userIsAdmin ? { opacity: 0.8 } : {},
                          minWidth: isMultiMonthMode ? 220 : undefined,
                          whiteSpace: isMultiMonthMode ? 'nowrap' : 'normal',
                        }}
                      >
                        <Tooltip
                          title={emp.emp_comment
                            ? <span style={{ whiteSpace: 'pre-line' }}>{emp.emp_comment}</span>
                            : ''}
                          placement="bottom-start"
                          arrow
                          enterDelay={0}
                          enterNextDelay={0}
                          TransitionProps={{ timeout: 0 }}
                        >
                          <span style={{
                            textDecoration: emp.emp_comment ? 'underline dotted' : 'none',
                            textUnderlineOffset: '4px',
                            cursor: 'inherit',
                          }}>
                            {emp.emp_name}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{emp.emp_dept}</TableCell>
                      <TableCell>{abbreviateWeekOff(emp.week_off_day)}</TableCell>

                      {isMultiMonthMode ? renderPaddedDayCells(emp) : emp.days.map(day => renderDayCell(emp, day))}

                      <TableCell align="center">{emp.half_present || 0}</TableCell>
                      <TableCell align="center">{emp.present || 0}</TableCell>
                      <TableCell align="center">{emp.absent || 0}</TableCell>
                      <TableCell align="center">
                        {(emp.half_leave || 0) > 0 ? (
                          <Tooltip title={`${emp.leave || 0} L + ${emp.half_leave} 1/2PL`}>
                            <span style={{ cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: '3px' }}>
                              {(emp.leave || 0) + 0.5 * emp.half_leave}
                            </span>
                          </Tooltip>
                        ) : (emp.leave || 0)}
                      </TableCell>
                      <TableCell align="center">{emp.holiday || 0}</TableCell>
                      <TableCell align="center">{emp.holiday_present || 0}</TableCell>
                      <TableCell align="center">{emp.holiday_half || 0}</TableCell>
                      <TableCell align="center">
                        {emp.week_off || 0}
                        {woInfo?.mismatch && (
                          <Tooltip title={`Week Off mismatch: ${getRowMonthLabel(emp)} has ${woInfo.baseExpected} ${emp.week_off_day}(s), adjusted to ${woInfo.expected} expected based on this employee's Total (${woInfo.total.toFixed(1)}) this month - but WO+WOP+WO1/2P totals ${woInfo.actual}.`}>
                            <Box component="span" sx={{ ml: 0.5, color: '#d97706', fontWeight: 'bold', cursor: 'help' }}>⚠️</Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">{emp.week_off_present || 0}</TableCell>
                      <TableCell align="center">{emp.week_off_half || 0}</TableCell>
                      
                      <TableCell>
                        <input
                          type="number"
                          value={flexiDays[emp.emp_id] || 0}
                          onChange={(e) => handleFlexiDaysChange(emp.emp_id, e.target.value)}
                          step={0.5}
                          disabled={isMultiMonthMode}
                          style={nativeInputStyle}
                        />
                      </TableCell>
                      <TableCell align="center"><strong>{calculateTotalWorkingDays(emp).toFixed(1)}</strong></TableCell>
                      <TableCell>
                        <input
                          type="number"
                          value={exDays[emp.emp_id] || 0}
                          onChange={(e) => handleExDaysChange(emp.emp_id, e.target.value)}
                          step={0.5}
                          disabled={isMultiMonthMode}
                          style={nativeInputStyle}
                        />
                      </TableCell>
                      <TableCell align="center">{emp.emp_ot_allowed ? (emp.week_off_present + 0.5*emp.week_off_half + emp.holiday_present + 0.5*emp.holiday_half) : '—'}</TableCell>
                      <TableCell>
                        <input
                          type="number"
                          value={otDetails[emp.emp_id]?.ot_hours ?? 0}
                          onChange={(e) => handleOtChange(emp.emp_id, 'ot_hours', e.target.value)}
                          step={0.5}
                          min={0}
                          disabled={isMultiMonthMode}
                          style={nativeInputStyle}
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          type="number"
                          value={otDetails[emp.emp_id]?.ot_amt ?? 0}
                          onChange={(e) => handleOtChange(emp.emp_id, 'ot_amt', e.target.value)}
                          step={1}
                          min={0}
                          disabled={isMultiMonthMode}
                          style={nativeInputStyle}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <strong>{Math.ceil(otDetails[emp.emp_id]?.ot_total ?? 0)}</strong>
                        {otDetails[emp.emp_id]?.gross_salary == null && (otDetails[emp.emp_id]?.ot_days ?? 0) > 0 && (
                          <Tooltip title="Incomplete: no Gross Salary configured for this employee, so OT Days isn't included in this total (treated as ₹0). Configure Salary to fix.">
                            <Box component="span" sx={{ ml: 0.5, color: '#d97706', fontWeight: 'bold', cursor: 'help' }}>⚠️</Box>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {userIsAdmin ? (
                          <select
                            value={otDetails[emp.emp_id]?.review_required ?? 'N'}
                            onChange={(e) => handleReviewChange(emp.emp_id, e.target.value)}
                            style={nativeSelectStyle}
                          >
                            <option value="Y">Y</option>
                            <option value="N">N</option>
                          </select>
                        ) : (
                          otDetails[emp.emp_id]?.review_required ?? 'N'
                        )}
                      </TableCell>
                      <TableCell>
                        <input
                          type="number"
                          value={otDetails[emp.emp_id]?.adv_amt ?? 0}
                          onChange={(e) => handleOtChange(emp.emp_id, 'adv_amt', e.target.value)}
                          step={1}
                          min={0}
                          disabled={isMultiMonthMode}
                          style={nativeInputStyle}
                        />
                      </TableCell>
                      <TableCell align="center">{emp.adv_given ? emp.adv_given.toLocaleString() : '—'}</TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary" onClick={() => handleSaveFlexiDays(emp.emp_id, emp.emp_name)} disabled={isMultiMonthMode || savingFlexiDays || !dirtyEmployees.has(emp.emp_id)} title="Save">
                          <Save fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell align="center">
                        {calculatePaySalary(emp) !== null ? (
                          <strong>{calculatePaySalary(emp).toLocaleString()}</strong>
                        ) : (
                          <Tooltip title="No Gross Salary configured for this employee.">
                            <Box component="span" sx={{ color: '#d97706', fontWeight: 'bold', cursor: 'help' }}>—</Box>
                          </Tooltip>
                        )}
                      </TableCell>

                      {/* Leave tracking data cells */}
                      <TableCell align="center" sx={{ bgcolor: '#fff3e0' }}>{emp.carried_forward_leaves ?? '—'}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#fff3e0' }}>{emp.leaves_per_annum ?? '—'}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#fff3e0' }}>{emp.total_leaves_available ?? '—'}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#fff3e0' }}>{emp.leave_earned ?? '—'}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: '#fff3e0', fontWeight: 'bold' }}>{emp.leave_availed ?? '—'}</TableCell>
                      <TableCell align="center" sx={{ bgcolor: emp.eligible_remaining < 0 ? '#ffebee' : '#fff3e0', fontWeight: 'bold', color: emp.eligible_remaining < 0 ? '#c62828' : 'inherit' }}>
                        {emp.eligible_remaining ?? '—'}
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: emp.leave_remaining < 0 ? '#ffebee' : '#fff3e0', fontWeight: 'bold', color: emp.leave_remaining < 0 ? '#c62828' : 'inherit' }}>
                        {emp.leave_remaining ?? '—'}
                      </TableCell>
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {reportData && reportData.data.length > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Status Codes:</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#ffffff', border: '1px solid #ccc' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>P - Present</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#ffffff', border: '1px solid #ccc' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>CO - Comp Off</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#ffffdd' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>1/2P - Half Present Half Absent</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#ffcdcd' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>A - Absent</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#dddddd' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>L - Leave</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#dddddd' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>1/2PL - Half Present Half Leave</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#dddddd' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>WO - Week Off</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#dddddd' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>H - Holiday</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#d9f6ff' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>WOP - Week Off Present</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#d9f6ff' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>HP - Holiday Present</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#d9f6ff' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>WO1/2P - Week Off Half Day</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#d9f6ff' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>H1/2P - Holiday Half Day</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#ffffdd', border: '5px solid #ffcdd2' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>Half P from a Missing Punch</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#ffffdd', border: '1px solid #ccc' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>Half P - Worked between Half-Day and Full-Day Threshold</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 20, height: 20, bgcolor: '#ffffdd', border: '5px solid #ffd79c' }} />
                  <Typography variant="body2" sx={{ fontSize: 12 }}>Half P - Worked between Absent Threshold and Half-Day Threshold</Typography>
                </Box>
              </Box>
              {userIsAdmin && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Note: Click on any day cell to adjust attendance status (Admin only)
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Container>

      {/* ── Employee Comment Dialog ─────────────────────────────────────── */}
      <Dialog
        open={commentDialogOpen}
        onClose={() => !savingComment && setCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Comment — <strong>{commentEmp?.emp_name}</strong> ({commentEmp?.emp_id})
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={5}
            label="Comment"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add any notes or remarks about this employee..."
            sx={{ mt: 1 }}
            disabled={savingComment}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentDialogOpen(false)} disabled={savingComment}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveComment}
            disabled={savingComment}
            startIcon={savingComment ? <CircularProgress size={16} color="inherit" /> : <Save />}
          >
            {savingComment ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Row-level Edit Dialog ────────────────────────────────────────── */}
      <Dialog
        open={rowEditDialogOpen}
        onClose={() => !savingRowEdits && setRowEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: '85vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Edit Attendance — <strong>{selectedEmp?.emp_name}</strong> ({selectedEmp?.emp_id}) — {months[month - 1]} {year}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0, overflow: 'auto', flex: 1 }}>
          <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: 80 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: 260 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white' }}>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedEmp?.days.map(day => {
                const edit    = rowEdits[day.date]        || { status: day.status, reason: '' };
                const initial = rowEditsInitial[day.date] || { status: day.status };
                const isModified = edit.status !== initial.status;

                return (
                  <TableRow key={day.date} sx={{ bgcolor: isModified ? '#fffde7' : edit.status === 'A' ? '#fff2f2' : (edit.status === 'WO' || edit.status === 'H') ? '#dddddd' : 'inherit' }}>
                    <TableCell sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      {formatDayHeader(day.date)}
                      {isModified && (
                        <Box component="span" sx={{ ml: 0.5, color: 'warning.dark', fontSize: '0.65rem' }}>●</Box>
                      )}
                    </TableCell>

                    <TableCell>
                      <FormControl fullWidth size="small">
                        <Select
                          value={edit.status}
                          onChange={(e) => handleRowEditChange(day.date, 'status', e.target.value)}
                          disabled={savingRowEdits}
                          sx={{ fontSize: '0.75rem' }}
                        >
                          {STATUS_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '0.75rem' }}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {day.is_adjusted && edit.status !== day.original_status && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.3 }}>
                          Original: {formatStatusDisplay(day.original_status)}
                        </Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Reason (optional)"
                        value={edit.reason}
                        inputProps={{ style: { fontSize: '0.75rem' } }}
                        onChange={(e) => handleRowEditChange(day.date, 'reason', e.target.value)}
                        disabled={savingRowEdits}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', px: 2, py: 1.5, borderTop: '1px solid rgba(0,0,0,0.12)' }}>
          <Typography variant="caption" color="text.secondary">
            {(() => {
              const count = selectedEmp?.days.filter(
                day => rowEdits[day.date]?.status !== rowEditsInitial[day.date]?.status
              ).length || 0;
              return count > 0 ? `${count} day(s) modified — only modified days will be saved` : 'No changes yet';
            })()}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={handleRevertAllToOriginal} disabled={savingRowEdits || !selectedEmp}>
              Revert All to Original
            </Button>
            <Button onClick={handleUndoRevertAll} disabled={savingRowEdits || !preRevertRowEdits}>
              Undo Revert All
            </Button>
            <Button onClick={() => setRowEditDialogOpen(false)} disabled={savingRowEdits}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveRowEdits}
              disabled={savingRowEdits}
              startIcon={savingRowEdits ? <CircularProgress size={16} color="inherit" /> : <Save />}
            >
              {savingRowEdits ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ── Cell-level Adjustment Dialog (existing, unchanged) ───────────── */}
      <Dialog open={adjustmentDialogOpen} onClose={() => !savingAdjustment && setAdjustmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Adjust Attendance - {selectedCell?.empName} - Day {selectedCell?.dayNumber}</DialogTitle>
        <DialogContent>
          {selectedCell?.isAdjusted && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Original Status: <strong>{formatStatusDisplay(selectedCell.originalStatus)}</strong>
            </Alert>
          )}

          {/* Also shown at the top of the page (line ~1075), but that's
              behind this modal - repeated here so a rejected save (e.g.
              "Leaves are not available for this employee") is actually
              visible without closing the dialog first. */}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} label="Status" disabled={savingAdjustment}>
              {STATUS_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <TextField
            fullWidth
            margin="normal"
            label="Reason (optional)"
            multiline
            rows={3}
            value={adjustmentReason}
            onChange={(e) => setAdjustmentReason(e.target.value)}
            placeholder="e.g., Approved leave, Manual entry - system error, Medical appointment"
            disabled={savingAdjustment}
          />
        </DialogContent>
        <DialogActions>
          {selectedCell?.isAdjusted && (
            <Button onClick={handleDeleteAdjustment} color="error" disabled={savingAdjustment}>Revert to Original</Button>
          )}
          <Button onClick={() => setAdjustmentDialogOpen(false)} disabled={savingAdjustment}>Cancel</Button>
          <Button onClick={handleSaveAdjustment} variant="contained" disabled={savingAdjustment}>
            {savingAdjustment ? 'Saving...' : 'Save Adjustment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendanceReport;